/**
 * Canonical per-reseller feature editor.
 *
 * Two entry points render this component:
 *   1. Super-admin drill-in: /resellers/:id/settings/features   → super-admin mode.
 *      Can set all three values per feature: enabled, owner→reseller price,
 *      reseller→customer price.
 *   2. Reseller self-view: /settings/features (via FeaturesReseller wrapper).
 *      Can only set `enabled` and `reseller_monthly_price_cents`. The
 *      owner→reseller price is rendered read-only.
 *
 * Data source: GET /api/feature-flags/by-reseller/:resellerId
 * Mutation:    PUT /api/feature-flags/by-reseller/:resellerId/:key
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { featureFlags, type ResellerEditorRow } from '@/api/featureFlags';
import { resellers } from '@/api/resellers';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import {
  Lock, Info, AlertCircle, Save, Loader2, ArrowLeft,
} from 'lucide-react';
import { FEATURE_FLAGS_QUERY_KEY } from '@/hooks/useFeatureFlag';

export interface ResellerFeaturesEditorProps {
  resellerId: string;
  /**
   * When true, the current user is the platform super-admin configuring
   * this reseller. When false, the reseller is viewing their own page:
   * owner→reseller price is read-only, no back-link to the resellers list.
   */
  superAdminMode: boolean;
}

function centsToDollar(c: number | null | undefined): string {
  return ((c ?? 0) / 100).toFixed(2);
}
function dollarToCents(s: string): number {
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

export function ResellerFeaturesEditor({ resellerId, superAdminMode }: ResellerFeaturesEditorProps) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['feature-flags', 'by-reseller', resellerId],
    queryFn: () => featureFlags.byReseller(resellerId),
  });

  const { data: resellerMeta } = useQuery({
    queryKey: ['reseller', resellerId],
    queryFn: () => resellers.get(resellerId),
    enabled: superAdminMode,
  });

  const rows = data?.rows ?? [];
  const nonCore = rows.filter((r) => !r.core);
  const coreRows = rows.filter((r) => r.core);
  const groups = useMemo(() => groupByCategory(nonCore), [nonCore]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['feature-flags'] });
    qc.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
  };

  return (
    <>
      <PageHeader
        eyebrow={
          superAdminMode
            ? (resellerMeta ? `Reseller · ${resellerMeta.name}` : 'Reseller settings')
            : 'Settings'
        }
        title="Feature flags"
        description={
          superAdminMode
            ? 'Enable features for this reseller and set the monthly price you charge them. They can set their own price for customers.'
            : 'Decide which Ivrello features to pay for and what to charge your customers for each one.'
        }
        actions={superAdminMode ? (
          <Button asChild variant="outline" size="sm">
            <Link to={`/resellers/${resellerId}`}>
              <ArrowLeft className="h-4 w-4" /> Back to reseller
            </Link>
          </Button>
        ) : undefined}
      />

      <Card className={`mb-4 border-l-4 ${superAdminMode ? 'border-l-emerald-400 bg-emerald-50/40' : 'border-l-blue-400 bg-blue-50/40'}`}>
        <CardContent className="py-3.5 flex items-start gap-3">
          <Info className={`h-4 w-4 shrink-0 mt-0.5 ${superAdminMode ? 'text-emerald-600' : 'text-blue-600'}`} />
          <div className={`text-xs space-y-1 ${superAdminMode ? 'text-emerald-900' : 'text-blue-900'}`}>
            {superAdminMode ? (
              <>
                <p>
                  <span className="font-semibold">Your price</span> is what the platform
                  charges this reseller each month. <span className="font-semibold">Their price</span> is
                  what they charge their customers.
                </p>
                <p className={superAdminMode ? 'text-emerald-800/80' : 'text-blue-800/80'}>
                  Turning a feature off here disables it for the reseller and all their customers.
                </p>
              </>
            ) : (
              <>
                <p>
                  <span className="font-semibold">Enabling a feature charges you</span>{' '}
                  the owner's monthly price. Set a price below to charge customers who enable it.
                </p>
                <p className="text-blue-800/80">
                  Turning a feature off here hides it entirely from your customers' portals.
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card><CardContent className="py-10 text-sm text-muted-foreground">Loading…</CardContent></Card>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <Card key={g.category}>
              <CardHeader>
                <CardTitle className="text-base">{g.category}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {g.rows.map((row) => (
                    <FeatureRow
                      key={row.key}
                      resellerId={resellerId}
                      row={row}
                      superAdminMode={superAdminMode}
                      onSaved={invalidate}
                    />
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}

          {coreRows.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Core features</CardTitle>
                <CardDescription>Always on — included at no extra charge.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {coreRows.map((row) => (
                    <li key={row.key} className="p-4 flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{row.display_name}</p>
                          <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 text-amber-700">
                            <Lock className="h-2.5 w-2.5" /> Always on
                          </Badge>
                        </div>
                        {row.description && (
                          <p className="text-xs text-muted-foreground mt-1">{row.description}</p>
                        )}
                      </div>
                      <Switch checked disabled />
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </>
  );
}

interface FeatureRowProps {
  resellerId: string;
  row: ResellerEditorRow;
  superAdminMode: boolean;
  onSaved: () => void;
}

function FeatureRow({ resellerId, row, superAdminMode, onSaved }: FeatureRowProps) {
  const [enabled, setEnabled] = useState(row.enabled);
  const [ownerPriceDollar, setOwnerPriceDollar] = useState(centsToDollar(row.owner_monthly_price_cents));
  const [resellerPriceDollar, setResellerPriceDollar] = useState(
    centsToDollar(row.reseller_monthly_price_cents ?? row.owner_monthly_price_cents),
  );
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: {
        enabled: boolean;
        owner_monthly_price_cents?: number;
        reseller_monthly_price_cents?: number;
      } = {
        enabled,
        reseller_monthly_price_cents: dollarToCents(resellerPriceDollar),
      };
      if (superAdminMode) {
        body.owner_monthly_price_cents = dollarToCents(ownerPriceDollar);
      }
      return featureFlags.setByReseller(resellerId, row.key, body);
    },
    onSuccess: () => {
      setDirty(false);
      setErr(null);
      onSaved();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Save failed'),
  });

  return (
    <li className="grid grid-cols-[1fr_auto] gap-4 p-4 items-start">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium">{row.display_name}</p>
          {row.source !== 'owner_default' && (
            <Badge variant="outline" className="text-[10px]">{sourceLabel(row.source, superAdminMode)}</Badge>
          )}
          {superAdminMode && row.owner_price_is_override && (
            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
              Custom price
            </Badge>
          )}
        </div>
        {row.description && (
          <p className="text-xs text-muted-foreground mt-1">{row.description}</p>
        )}

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-[auto_auto_auto] gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {superAdminMode ? 'Your price (owner → reseller)' : 'Your cost'}
            </Label>
            {superAdminMode ? (
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input
                  type="number" min="0" step="0.01"
                  value={ownerPriceDollar}
                  onChange={(e) => { setOwnerPriceDollar(e.target.value); setDirty(true); }}
                  className="h-8 w-28 pl-5 text-sm font-mono"
                />
              </div>
            ) : (
              <p className="text-sm font-mono h-8 flex items-center">
                ${centsToDollar(row.owner_monthly_price_cents)}
                <span className="text-muted-foreground text-xs ml-1">/mo</span>
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {superAdminMode ? 'Their price (reseller → customers)' : 'Charge customers'}
            </Label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <Input
                type="number" min="0" step="0.01"
                value={resellerPriceDollar}
                onChange={(e) => { setResellerPriceDollar(e.target.value); setDirty(true); }}
                className="h-8 w-28 pl-5 text-sm font-mono"
              />
            </div>
          </div>
          {dirty && (
            <Button
              size="sm" className="h-8 text-xs"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
          )}
        </div>

        {err && (
          <p role="alert" className="text-xs text-destructive mt-2 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {err}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <Switch
          checked={enabled}
          onCheckedChange={(v) => { setEnabled(v); setDirty(true); }}
        />
        <span className="text-[10px] text-muted-foreground">{enabled ? 'Enabled' : 'Off'}</span>
      </div>
    </li>
  );
}

function sourceLabel(s: ResellerEditorRow['source'], superAdminMode: boolean): string {
  if (s === 'customer') return 'Customer override';
  if (s === 'reseller') return superAdminMode ? 'Reseller override' : 'You set this';
  if (s === 'core') return 'Core';
  return 'Owner default';
}

function groupByCategory(rows: ResellerEditorRow[]) {
  const order: Record<string, number> = { Communication: 0, Engagement: 1, Commerce: 2, Core: 99 };
  const map = new Map<string, ResellerEditorRow[]>();
  for (const r of rows) {
    if (!map.has(r.category)) map.set(r.category, []);
    map.get(r.category)!.push(r);
  }
  return Array.from(map.entries())
    .map(([category, rows]) => ({ category, rows: [...rows].sort((a, b) => a.sort_order - b.sort_order) }))
    .sort((a, b) => (order[a.category] ?? 50) - (order[b.category] ?? 50));
}
